/**
 * Agents feature — query + mutation hooks.
 *
 * Tenant-scoped surface: /api/v1/tenants/{tenant_id}/agents[/...]
 * Hooks take the resolved tenant UUID (not the slug). Mutations are wrapped in
 * withStepUp because agents.manage / agents.certify are step-up-required on the
 * backend (rbac.go).
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DimMeta } from "@pureid/auth-core";
import { apiFetch, NotFoundError, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  agentSchema,
  agentRevokeResponseSchema,
  killPrincipalResponseSchema,
  killSwitchResultSchema,
  type KillSwitchResult,
  cnfPinRequestSchema,
  type CnfPinRequest,
  agentsListResponseSchema,
  grantEvidenceSchema,
  chainVerifySchema,
  grantsListResponseSchema,
  protectedResourceListSchema,
  resourceCustodySchema,
  type ResourceCustody,
  resourceOperationListSchema,
  resourceOperationSchema,
  type ResourceOperation,
  type RegisterOperationInput,
  type UpdateOperationInput,
  protectedResourceSchema,
  type GrantsListResponse,
  type ProtectedResourceList,
  type ProtectedResource,
  type ResourceRegistryStatus,
  type CreateProtectedResourceInput,
  type UpdateProtectedResourceInput,
  type Agent,
  type AgentLifecycleState,
  type AgentRevokeResponse,
  type KillPrincipalResponse,
  stopPreviewSchema,
  type StopPreview,
  pausePrincipalResponseSchema,
  type PausePrincipalResponse,
  resumePrincipalResponseSchema,
  type ResumePrincipalResponse,
  type GrantEvidence,
  type ChainVerifyResult,
  type AgentsListResponse,
  type AgentsListSearch,
  type CreateAgentInput,
  type UpdateAgentInput,
  recertifyAgentResponseSchema,
  type RecertifyAgentInput,
  type RecertifyAgentResponse,
  type ReassignOwnerInput,
  type CreateWorkloadIdentityInput,
  budgetBreakdownSchema,
  type BudgetBreakdown,
  crewViewSchema,
  type CrewView,
  standingDelegationsListSchema,
  createStandingDelegationResponseSchema,
  type StandingDelegationsList,
  type CreateStandingDelegationInput,
  type CreateStandingDelegationResponse,
  simulatePolicyResponseSchema,
  type SimulatePolicyInput,
  type SimulatePolicyResponse,
  dimensionCatalogResponseSchema,
  type DimensionCatalogEntry,
  budgetChainRollupSchema,
  type BudgetChainRollup,
  agentEnrollmentProvenanceSchema,
  type AgentEnrollmentProvenance,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agents`;
}

// ── Fetchers ────────────────────────────────────────────────────────────────

export async function fetchAgentsList(
  tenantID: string,
  search: AgentsListSearch,
): Promise<AgentsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: { state: search.state, kind: search.kind, q: search.q, offset: search.offset },
  });
  return agentsListResponseSchema.parse(raw);
}

// The number of agents one typeahead search returns — the picker shows the top matches and a
// "refine" hint, never the whole roster (mirrors the user search's top-N contract).
const AGENT_SEARCH_LIMIT = 20;

// searchAgents is the relevance-ranked agent typeahead adapter for the Activity scope picker. It hits
// the same list endpoint with `q` (trigram-ranked server-side) + a small limit, and returns the top
// matches, the exact total, and a hasMore flag so the combobox can show "top N of M — refine". Below
// the server's 3-char trigram floor the endpoint returns nothing (the picker gates at the same floor).
export async function searchAgents(
  tenantID: string,
  q: string,
): Promise<{ items: Agent[]; total: number; hasMore: boolean }> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: { q, limit: AGENT_SEARCH_LIMIT },
  });
  const parsed = agentsListResponseSchema.parse(raw);
  return {
    items: parsed.data,
    total: parsed.pagination.total,
    hasMore: parsed.pagination.total > parsed.data.length,
  };
}

// Cheap "does any delegable agent exist?" probe — asks the backend's delegable
// filter for a single row and reads pagination.total. Accurate regardless of how
// many agents exist (the list scan only saw page 1).
export async function fetchHasDelegableAgent(tenantID: string): Promise<boolean> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: { delegable: "true", limit: 1 },
  });
  return agentsListResponseSchema.parse(raw).pagination.total > 0;
}

export async function fetchAgent(tenantID: string, agentId: string): Promise<Agent> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(agentId)}`,
  );
  return agentSchema.parse(raw);
}

// ── Queries ──────────────────────────────────────────────────────────────────

export function useAgentsQuery(tenantID: string, search: AgentsListSearch) {
  return useQuery<AgentsListResponse, ApiError>({
    queryKey: queryKeys.agents.list(tenantID, search),
    queryFn: () => fetchAgentsList(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useHasDelegableAgentQuery(tenantID: string) {
  return useQuery<boolean, ApiError>({
    queryKey: [...queryKeys.agents.all, "delegable-exists", tenantID] as const,
    queryFn: () => fetchHasDelegableAgent(tenantID),
    enabled: tenantID.length > 0,
  });
}

export function useAgentQuery(tenantID: string, agentId: string) {
  return useQuery<Agent, ApiError>({
    queryKey: queryKeys.agents.detail(tenantID, agentId),
    queryFn: () => fetchAgent(tenantID, agentId),
    enabled: tenantID.length > 0 && agentId.length > 0,
  });
}

/** The pairing provenance for a self-paired agent, or null when the agent has no pairing record
 *  (operator-provisioned / federated — the backend 404s, which we normalize to null so the UI simply
 *  omits the card). */
export async function fetchAgentEnrollment(
  tenantID: string,
  agentId: string,
): Promise<AgentEnrollmentProvenance | null> {
  try {
    const raw = await apiFetch<unknown>(
      `${base(tenantID)}/${encodeURIComponent(agentId)}/enrollment`,
    );
    return agentEnrollmentProvenanceSchema.parse(raw);
  } catch (err) {
    if (err instanceof NotFoundError) return null;
    throw err;
  }
}

export function useAgentEnrollmentQuery(tenantID: string, agentId: string) {
  return useQuery<AgentEnrollmentProvenance | null, ApiError>({
    queryKey: queryKeys.agents.enrollment(tenantID, agentId),
    queryFn: () => fetchAgentEnrollment(tenantID, agentId),
    enabled: tenantID.length > 0 && agentId.length > 0,
    staleTime: 60_000,
  });
}

/**
 * useBudgetChainRollupQuery reads the cross-identity carried-spend rollup for one agent (Item 3,
 * budget-reset gaming remediation): its lineage chain plus per-dimension Total / Self / Carried
 * spend. Surfaces the CARRIED history a re-provisioned agent's own utilization read (not lineage-
 * widened) would otherwise show as $0 — so the dashboard matches the reality the anti-silence gate
 * enforces. Read-only oversight (agents.read). Best-effort: an error/absence just hides the panel.
 */
export function useBudgetChainRollupQuery(
  tenantID: string,
  agentId: string,
  opts?: { enabled?: boolean },
) {
  return useQuery<BudgetChainRollup, ApiError>({
    queryKey: queryKeys.agents.chainRollup(tenantID, agentId),
    queryFn: () =>
      apiFetch<unknown>(
        `${base(tenantID)}/${encodeURIComponent(agentId)}/budget-chain-rollup`,
      ).then((raw) => budgetChainRollupSchema.parse(raw)),
    enabled: (opts?.enabled ?? true) && tenantID.length > 0 && agentId.length > 0,
    staleTime: 60_000,
  });
}

// The tenant's declared consequence-dimension catalog — the units a policy author picks from. Stable
// tenant config, so it caches like a reference list. DEFINING a new unit is the separate SoD-gated
// declare; this is read-class (agents.read).
export async function fetchDimensionCatalog(tenantID: string): Promise<DimensionCatalogEntry[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/consequence-dimensions`);
  return dimensionCatalogResponseSchema.parse(raw).dimensions;
}

export function useDimensionCatalogQuery(tenantID: string) {
  return useQuery<DimensionCatalogEntry[], ApiError>({
    queryKey: [...queryKeys.agents.all, "dimension-catalog", tenantID] as const,
    queryFn: () => fetchDimensionCatalog(tenantID),
    enabled: tenantID.length > 0,
  });
}

// useTenantUnits adapts the dimension catalog into the two shapes the policy editor needs: `units` (the
// UnitChooser catalog) and `dimMeta` (the key→DimMeta map that scales caps and drives the amount inputs).
// Memoized on the query data so the editor doesn't re-derive them every render.
export function useTenantUnits(tenantID: string): {
  units: Array<{ key: string; meta: DimMeta }>;
  dimMeta: Map<string, DimMeta>;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useDimensionCatalogQuery(tenantID);
  return useMemo(() => {
    const units = (q.data ?? []).map((e) => ({
      key: e.key,
      meta: {
        displayName: e.display_name,
        exponent: e.exponent,
        unitCode: e.unit_code ? e.unit_code : undefined,
      } satisfies DimMeta,
    }));
    return {
      units,
      dimMeta: new Map(units.map((u) => [u.key, u.meta])),
      isLoading: q.isLoading,
      isError: q.isError,
    };
  }, [q.data, q.isLoading, q.isError]);
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateAgentMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, CreateAgentInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), { method: "POST", body: input }).then(
          (raw) => agentSchema.parse(raw),
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * Policy simulator — "what would this action do?" answered by the SAME effective-policy
 * resolution + per-action gates the mint/PEP run (backend agent/simulate.go). A mutation
 * only in mechanics (the POST body carries the hypothetical + optional draft ceiling);
 * semantically a pure read — nothing persists, nothing to invalidate, no step-up
 * (agents.read-gated server-side).
 */
export function useSimulatePolicyMutation(tenantID: string, agentId: string) {
  return useMutation<SimulatePolicyResponse, ApiError, SimulatePolicyInput>({
    mutationFn: (input) =>
      apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/policy-simulate`, {
        method: "POST",
        body: input,
      }).then((raw) => simulatePolicyResponseSchema.parse(raw)),
  });
}

export function useUpdateAgentMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, UpdateAgentInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}`, {
          method: "PATCH",
          body: input,
        }).then((raw) => agentSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/** Sets or clears the Edge group that serves the agent, whose address it dials. */
export function useSetAgentEdgeGroupMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, string | null>({
    mutationFn: (groupID) =>
      apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/edge-group`, {
        method: "PUT",
        body: { group_id: groupID },
      }).then((raw) => agentSchema.parse(raw)),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useSetAgentCnfPinMutation pins or rotates the agent's proof-of-possession key (PUT
 * .../agents/{id}/cnf-pin): the key every grant it mints must be bound to. agents.manage with
 * step-up. The body is checked against the contract before the request; the updated agent replaces
 * the cached record.
 */
export function useSetAgentCnfPinMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, CnfPinRequest>({
    mutationFn: (pin) => {
      const body = cnfPinRequestSchema.parse(pin);
      return withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/cnf-pin`, {
          method: "PUT",
          body,
        }).then((raw) => agentSchema.parse(raw)),
      );
    },
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

export function useSetAgentStateMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, AgentLifecycleState>({
    mutationFn: (state) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/state`, {
          method: "POST",
          body: { state },
        }).then((raw) => agentSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useRevokeAgentMutation fires the per-agent KILL-SWITCH (POST .../agents/{id}/revoke):
 * it invalidates ALL of the agent's outstanding grants so its next edge call is
 * denied. Distinct from a lifecycle suspend — it acts on the grants, not the record's
 * state. Step-up-gated (agent_grants.kill_switch). Refreshes the detail + list.
 */
export function useRevokeAgentMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<AgentRevokeResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/revoke`, {
          method: "POST",
          body: {},
        }).then((raw) => agentRevokeResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.detail(tenantID, agentId) });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useKillPrincipalMutation fires the COMPOSED kill-principal verb (POST .../agents/{id}/kill): one
 * action = kill-switch watermark (live grants dead) + lifecycle suspend + revoke every standing
 * delegation, audited as one event. The fuller containment vs the grants-only kill-switch — for an
 * incident where the principal itself must stop, not just its current grants. Step-up-gated
 * (agent_grants.kill_switch). Returns the per-mechanism outcome; refreshes the detail + list.
 */
export function useKillPrincipalMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<KillPrincipalResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/kill`, {
          method: "POST",
          body: {},
        }).then((raw) => killPrincipalResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.detail(tenantID, agentId) });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useKillAgentMutation is useKillPrincipalMutation for a LIST, taking the agent id as the
 * mutation variable rather than binding it when the hook is created.
 *
 * It exists because Live authority needs the kill and could not have it. That surface is the
 * rail's 3am entry, `sidebar.tsx` literally calls it "THE 3AM ENTRY", and it is named for
 * cutting things — but every action on it was per-GRANT, and revoking a grant does not stop an
 * agent: the agent re-mints a fresh one from any standing delegation it holds, which the revoke
 * dialog says in as many words. The only real stop lived on the agent detail page behind a
 * "More" button, five clicks and two correct guesses from a cold start.
 *
 * A hook bound to one agent cannot serve a list, and hooks cannot be called per row, so this
 * takes the id at call time. Same endpoint, same step-up gate (agent_grants.kill_switch), same
 * composed verb: kill-switch watermark, lifecycle suspend, and every standing delegation
 * revoked, audited as one event.
 */
export function useKillAgentMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<KillPrincipalResponse, ApiError, string>({
    mutationFn: (agentId) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/kill`, {
          method: "POST",
          body: {},
        }).then((raw) => killPrincipalResponseSchema.parse(raw)),
      ),
    onSuccess: (_res, agentId) => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.detail(tenantID, agentId) });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useFireAgentKillSwitchMutation fires the TENANT kill-switch (POST .../agents/kill-switch): the
 * tenant revocation watermark advances, so every agent grant minted up to now is refused at each
 * Edge's next liveness check. It is a flush, not a freeze: grants minted afterwards are unaffected,
 * so an agent with a standing delegation mints again. Served on the platform tree only (the tenant
 * surface deliberately has no slot for it). agent_grants.kill_switch with step-up.
 */
export function useFireAgentKillSwitchMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<KillSwitchResult, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/kill-switch`, { method: "POST" }).then((raw) =>
          killSwitchResultSchema.parse(raw),
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents", "grants", tenantID] });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useStopPreviewQuery fetches the blast-radius preview for the stop/pause dialogs
 * (GET .../agents/{id}/stop-preview). Read-class (agents.read) — an operator weighing a stop can
 * see what falls without holding the trigger permission. Enabled only while a dialog is open, and
 * never cached across opens (staleTime 0, tiny gcTime): a preview must describe NOW.
 */
export function useStopPreviewQuery(tenantID: string, agentId: string, enabled: boolean) {
  return useQuery<StopPreview, ApiError>({
    queryKey: queryKeys.agents.stopPreview(tenantID, agentId),
    queryFn: () =>
      apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/stop-preview`).then(
        (raw) => stopPreviewSchema.parse(raw),
      ),
    enabled: enabled && tenantID.length > 0 && agentId.length > 0,
    staleTime: 0,
    gcTime: 15_000,
  });
}

/**
 * usePauseAgentMutation fires the REVERSIBLE stop (POST .../agents/{id}/pause): lifecycle suspend
 * (no new grant can mint) + every active standing delegation paused, one audited action. The middle
 * rung between revoking a grant (too weak — the standing re-mints) and the composed kill (terminal —
 * standing-revoke is one-way and every human must re-delegate). NOT a kill: no watermark, so
 * already-minted grants finish on their own clock. Step-up-gated (agent_grants.kill_switch).
 */
export function usePauseAgentMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<PausePrincipalResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/pause`, {
          method: "POST",
          body: {},
        }).then((raw) => pausePrincipalResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.detail(tenantID, agentId) });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useResumeAgentMutation reverses a pause (POST .../agents/{id}/resume): standing delegations are
 * restored FIRST, then the lifecycle reactivates — so minting reopens only once the authority
 * beneath it is back. Returns standings_conflicted: a delegation slot re-taken while paused stays
 * paused and is counted, never silently left off — the caller must surface it.
 * Step-up-gated (agent_grants.kill_switch).
 */
export function useResumeAgentMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<ResumePrincipalResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/resume`, {
          method: "POST",
          body: {},
        }).then((raw) => resumePrincipalResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.detail(tenantID, agentId) });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useGrantEvidenceQuery fetches the signed, hash-chained decision trail for one grant
 * (by jti): the grant header + every edge authorization on it, with per-row
 * co-sign status and a chain-intact check. Disabled until a jti is supplied (the
 * operator pastes the jti the agent printed, or clicks one from the activity).
 */
export function useGrantEvidenceQuery(tenantID: string, jti: string) {
  return useQuery<GrantEvidence, ApiError>({
    queryKey: queryKeys.agents.evidence(tenantID, jti),
    queryFn: () =>
      apiFetch<unknown>(`${base(tenantID)}/grants/${encodeURIComponent(jti)}/evidence`).then((raw) =>
        grantEvidenceSchema.parse(raw),
      ),
    enabled: jti.trim().length > 0,
    staleTime: 30_000,
  });
}

/**
 * useVerifyChainQuery runs the whole-log integrity proof (GET .../agents/evidence/verify):
 * the backend independently re-verifies the ENTIRE tenant evidence chain (hash-linkage +
 * seq-contiguity + both ES256 signatures) and returns ok / the first offending seq. Lazy —
 * `enabled` is driven by an explicit "Verify chain" click, not page load (it scans the
 * whole chain). Not cached aggressively: a fresh verdict each time it's requested.
 */
export function useVerifyChainQuery(tenantID: string, enabled: boolean) {
  return useQuery<ChainVerifyResult, ApiError>({
    queryKey: queryKeys.agents.chainVerify(tenantID),
    queryFn: () =>
      apiFetch<unknown>(`${base(tenantID)}/evidence/verify`).then((raw) =>
        chainVerifySchema.parse(raw),
      ),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * fetchEvidenceBundle downloads the self-contained, offline-verifiable export bundle for
 * one grant (rows + the tenant's evidence public keys). Returned as the raw object so the
 * caller can hand it to the browser as a JSON file download — it is NOT cached (an
 * archival artifact, not view state).
 */
export async function fetchEvidenceBundle(tenantID: string, jti: string): Promise<unknown> {
  return apiFetch<unknown>(
    `${base(tenantID)}/grants/${encodeURIComponent(jti)}/evidence/export`,
  );
}

/**
 * fetchConsentEnvelope downloads the consent envelope DOCUMENT for the consent this grant
 * is anchored to — the exact canonical bytes the delegating human's passkey signed, the
 * offline companion to the evidence bundle (`evidence-verify -envelope`). 404 with a
 * legible reason is an EXPECTED answer, not a failure: an attended grant has no standing,
 * and an admin-provisioned/short-term/amended standing signed no envelope — those grants'
 * evidence is honestly "anchored by id only". Not cached (an archival artifact, like the
 * bundle), returned raw for the file download.
 */
export async function fetchConsentEnvelope(tenantID: string, jti: string): Promise<unknown> {
  return apiFetch<unknown>(
    `${base(tenantID)}/grants/${encodeURIComponent(jti)}/consent-envelope`,
  );
}

/**
 * useGrantsQuery lists the tenant's agent grants with EFFECTIVE lifecycle status
 * (active / expired / revoked-by-{grant,kill-switch}). includeAll=false shows only the
 * operationally-live (non-expired) set. Grants are short-lived, so it polls at 60s (the
 * device/agent freshness tier) and stays fresh for 15s.
 */
// Exported so the Active-grants route can prefetch (warm the table on navigation),
// matching the sibling agent routes' loader-prefetch idiom.
/**
 * The grants page size, and it is the server's MAXIMUM on purpose (grantstore.ListGrants
 * clamps anything above 200 back down to 50).
 *
 * Until 2026-08-15 the console sent neither `limit` nor `offset`, so it silently took the
 * server default of 50 and rendered whatever came back with no way to reach row 51. The
 * list has no search either, so at 3am the grant an operator came to kill could simply not
 * be on screen — and the footer's advice was to "revoke or let some expire to narrow the
 * list", i.e. to revoke unrelated live authority until the target appeared. Both halves of
 * that are now fixed: a full page is fetched, the rest is reachable by paging, and the
 * section filters what it holds.
 */
export const GRANTS_PAGE_SIZE = 200;

export function fetchGrants(
  tenantID: string,
  includeAll: boolean,
  offset = 0,
): Promise<GrantsListResponse> {
  return apiFetch<unknown>(`${base(tenantID)}/grants`, {
    searchParams: {
      ...(includeAll ? { state: "all" } : {}),
      limit: String(GRANTS_PAGE_SIZE),
      offset: String(offset),
    },
  }).then((raw) => grantsListResponseSchema.parse(raw));
}

export function useGrantsQuery(tenantID: string, includeAll: boolean, offset = 0) {
  return useQuery<GrantsListResponse, ApiError>({
    queryKey: queryKeys.agents.grants(tenantID, includeAll, offset),
    queryFn: () => fetchGrants(tenantID, includeAll, offset),
    staleTime: 15_000,
    refetchInterval: 60_000,
    // Paging must not blank the list: an operator mid-incident who loses every row while
    // the next page loads has lost the thing they were reading.
    placeholderData: (prev) => prev,
  });
}

/**
 * useRevokeGrantMutation revokes ONE grant by jti (POST .../agents/grants/{jti}/revoke) —
 * the cascade trigger also revokes its sub-delegations. Distinct from the per-agent
 * kill-switch (which acts on all of an agent's grants). Step-up-gated (agent_grants.revoke).
 */
export function useRevokeGrantMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (jti) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/grants/${encodeURIComponent(jti)}/revoke`, {
          method: "POST",
          body: { reason: "manual" },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents", "grants", tenantID] });
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useProtectedResourcesQuery lists the tenant's registered protected resources (the registry the
 * Edge serves), filtered and paged by the caller.
 */
// Exported so the Protected-resources route can prefetch (see fetchGrants).
export interface ResourceInventoryFilter { page?: number; page_size?: number; q?: string; application_id?: string; protocol?: "https" | "ssh" | "sftp" | "rdp" }
export function fetchProtectedResources(tenantID: string, filter: ResourceInventoryFilter = {}): Promise<ProtectedResourceList> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) { if (value !== undefined && value !== "") params.set(key, String(value)); }
  return apiFetch<unknown>(`${base(tenantID)}/protected-resources${params.size ? `?${params}` : ""}`).then((raw) =>
    protectedResourceListSchema.parse(raw),
  );
}

export function useProtectedResourcesQuery(tenantID: string, enabled = true, filter: ResourceInventoryFilter = {}) {
  return useQuery<ProtectedResourceList, ApiError>({
    queryKey: [...queryKeys.agents.protectedResources(tenantID), filter],
    enabled: enabled && tenantID.length > 0,
    queryFn: () => fetchProtectedResources(tenantID, filter),
    staleTime: 30_000,
  });
}

// OUTBOUND CREDENTIAL CUSTODY. The read carries no secret; the mutations take the
// secret ONCE in their input and hand it to the request — it is never written to the query cache,
// and the component clears its field the moment the promise resolves (CLAUDE.md: secrets never
// enter a cache).
export function fetchResourceCustody(tenantID: string, resource: string): Promise<ResourceCustody> {
  return apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/custody`).then(
    (raw) => resourceCustodySchema.parse(raw),
  );
}

export function useResourceCustodyQuery(tenantID: string, resource: string) {
  return useQuery<ResourceCustody, ApiError>({
    queryKey: queryKeys.agents.resourceCustody(tenantID, resource),
    queryFn: () => fetchResourceCustody(tenantID, resource),
    staleTime: 30_000,
  });
}

export type PlaceCustodyInput = { scheme: string; credential: string };

function invalidateCustody(qc: ReturnType<typeof useQueryClient>, tenantID: string, resource: string) {
  qc.invalidateQueries({ queryKey: queryKeys.agents.resourceCustody(tenantID, resource) });
  qc.invalidateQueries({ queryKey: queryKeys.agents.protectedResources(tenantID) });
}

// Place a credential on an egress destination that holds none. agents.manage + step-up + an
// interactive session.
export function usePlaceCustodyMutation(tenantID: string, resource: string) {
  const qc = useQueryClient();
  return useMutation<ResourceCustody, ApiError, PlaceCustodyInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/custody`, {
          method: "PUT",
          body: { scheme: input.scheme, credential: input.credential },
        }).then((raw) => resourceCustodySchema.parse(raw)),
      ),
    onSuccess: () => invalidateCustody(qc, tenantID, resource),
  });
}

// Rotate: retire the credential that stands and seal the new one.
export function useRotateCustodyMutation(tenantID: string, resource: string) {
  const qc = useQueryClient();
  return useMutation<ResourceCustody, ApiError, PlaceCustodyInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/custody/rotate`, {
          method: "POST",
          body: { scheme: input.scheme, credential: input.credential },
        }).then((raw) => resourceCustodySchema.parse(raw)),
      ),
    onSuccess: () => invalidateCustody(qc, tenantID, resource),
  });
}

// Withdraw: retire the credential; the destination forwards with nothing injected.
export function useWithdrawCustodyMutation(tenantID: string, resource: string) {
  const qc = useQueryClient();
  return useMutation<ResourceCustody, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/custody`, {
          method: "DELETE",
        }).then((raw) => resourceCustodySchema.parse(raw)),
      ),
    onSuccess: () => invalidateCustody(qc, tenantID, resource),
  });
}

// Register a protected resource in the DB registry. agents.manage + step-up.
export function useCreateProtectedResourceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ProtectedResource, ApiError, CreateProtectedResourceInput>({
    mutationFn: (input) => {
      const body: Record<string, unknown> = {
        resource: input.resource,
        protocol: input.protocol ?? "https",
        audience: input.audience,
        upstream_url: input.upstream_url,
        direction: input.direction,
        data_class: input.data_class,
        server_cert_sha256: input.server_cert_sha256,
        // ALWAYS SENT, never conditional. The server reads this field with replace semantics —
        // an absent key is a false, not an "unchanged" — so omitting it when the toggle is off
        // is the same wire as sending false, and omitting it when the toggle is ON silently
        // drops the opt-in while the toast reports success.
        private_access: input.private_access ?? false,
        private_label: input.private_access ? input.private_label?.trim() : undefined,
        require_posture: Boolean(input.private_access && input.require_posture),
        // ALWAYS SENT, like private_access: an edit that omitted them would switch native access
        // off, or leave "approve each connection" shown on and stored off.
        native_ssh_enabled: input.native_ssh_enabled ?? false,
        require_connection_approval: input.require_connection_approval ?? false,
        ssh_auth_method: input.ssh_auth_method,
        ssh_host_key: input.ssh_host_key?.trim(),
        ssh_login: input.ssh_login?.trim(),
        rdp_login: input.rdp_login?.trim(),
        rdp_domain: input.rdp_domain?.trim(),
      };
      if (input.egress?.trim()) body.egress = input.egress.trim();
      if (input.ca_cert_pem?.trim()) body.ca_cert_pem = input.ca_cert_pem;
      if (input.application_id?.trim()) body.application_id = input.application_id.trim();
      return withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources`, { method: "POST", body }).then(
          (raw) => protectedResourceSchema.parse(raw),
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.protectedResources(tenantID) });
    },
  });
}

// ── Operations on a resource ───────────────────────────────────────────────────────────────
// Read on demand when a row's operations are opened; never polled — an operation changes only
// when an administrator changes it here.
export function useResourceOperationsQuery(tenantID: string, resource: string, enabled: boolean) {
  return useQuery<ResourceOperation[], ApiError>({
    queryKey: queryKeys.agents.resourceOperations(tenantID, resource),
    queryFn: () =>
      apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/operations`).then(
        (raw) => resourceOperationListSchema.parse(raw).operations,
      ),
    enabled,
    staleTime: 30_000,
  });
}

// Register an operation on a resource. agents.manage + step-up, like registering the resource:
// `reversible` decides the approval tier of every future call, so this is a permission change.
export function useRegisterOperationMutation(tenantID: string, resource: string) {
  const qc = useQueryClient();
  return useMutation<ResourceOperation, ApiError, RegisterOperationInput>({
    mutationFn: (input) => {
      const body: Record<string, unknown> = {
        operation: input.operation,
        reversible: input.reversible,
        http_method: input.http_method,
        path_pattern: input.path_pattern,
        magnitude_fields: input.magnitude_fields,
      };
      if (input.counterparty_param?.trim()) body.counterparty_param = input.counterparty_param.trim();
      if (input.data_class_floor?.trim()) body.data_class_floor = input.data_class_floor.trim();
      return withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/operations`, {
          method: "POST",
          body,
        }).then((raw) => resourceOperationSchema.parse(raw)),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.resourceOperations(tenantID, resource) });
    },
  });
}

// Change an operation in place. The body is a PATCH: only the fields named change; the magnitude
// map is replaced whole when present.
export function useUpdateOperationMutation(tenantID: string, resource: string) {
  const qc = useQueryClient();
  return useMutation<ResourceOperation, ApiError, { operation: string; input: UpdateOperationInput }>({
    mutationFn: ({ operation, input }) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/operations/${encodeURIComponent(operation)}`,
          { method: "PATCH", body: input },
        ).then((raw) => resourceOperationSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.resourceOperations(tenantID, resource) });
    },
  });
}

// Update a resource's trust/routing fields (resource/audience/direction are immutable).
export function useUpdateProtectedResourceMutation(tenantID: string, resource: string) {
  const qc = useQueryClient();
  return useMutation<ProtectedResource, ApiError, UpdateProtectedResourceInput>({
    mutationFn: (input) => {
      const body: Record<string, unknown> = {
        upstream_url: input.upstream_url,
        data_class: input.data_class,
        server_cert_sha256: input.server_cert_sha256,
        // ALWAYS SENT — omission would be the dangerous half. The PUT replaces
        // the row, so a body with no private_access revokes an existing opt-in: an edit that
        // only rotated a cert pin would have closed a live application's private door and
        // written an audit row that reads as a decision nobody made.
        private_access: input.private_access ?? false,
        private_label: input.private_access ? input.private_label?.trim() : undefined,
        require_posture: Boolean(input.private_access && input.require_posture),
        // ALWAYS SENT, like private_access: an edit that omitted them would switch native access
        // off, or leave "approve each connection" shown on and stored off.
        native_ssh_enabled: input.native_ssh_enabled ?? false,
        require_connection_approval: input.require_connection_approval ?? false,
        ssh_auth_method: input.ssh_auth_method,
        ssh_host_key: input.ssh_host_key?.trim(),
        ssh_login: input.ssh_login?.trim(),
        rdp_login: input.rdp_login?.trim(),
        rdp_domain: input.rdp_domain?.trim(),
      };
      if (input.egress?.trim()) body.egress = input.egress.trim();
      if (input.ca_cert_pem?.trim()) body.ca_cert_pem = input.ca_cert_pem;
      if (input.application_id?.trim()) body.application_id = input.application_id.trim();
      return withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}`, {
          method: "PUT",
          body,
        }).then((raw) => protectedResourceSchema.parse(raw)),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.protectedResources(tenantID) });
    },
  });
}

// Enable / disable / delete a resource (disable & delete stop the edge serving it).
export function useSetProtectedResourceStatusMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ProtectedResource, ApiError, { resource: string; status: ResourceRegistryStatus }>({
    mutationFn: ({ resource, status }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/protected-resources/${encodeURIComponent(resource)}/status`, {
          method: "POST",
          body: { status },
        }).then((raw) => protectedResourceSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.protectedResources(tenantID) });
    },
  });
}

export function useCertifyAgentMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, { review_due_at?: string } | void>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/certify`, {
          method: "POST",
          body: input ?? {},
        }).then((raw) => agentSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useRecertifyAgentMutation TIGHTENS a certified/active agent's frozen ceilings (never widens).
 * POST .../agents/{id}/recertify — gated on agents.certify + step-up (same as certify; re-cert is
 * a governance act on the frozen ceiling, downward-only). Returns the updated agent + the
 * per-dimension diff of what was narrowed (surfaced to the operator as confirmation). A widen or a
 * no-op is a 422 the caller shows as an error; the mutation never optimistically narrows the cache.
 */
export function useRecertifyAgentMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<RecertifyAgentResponse, ApiError, RecertifyAgentInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/recertify`, {
          method: "POST",
          body: input,
        }).then((raw) => recertifyAgentResponseSchema.parse(raw)),
      ),
    onSuccess: (res) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), res.agent);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useCreateWorkloadIdentityMutation registers a workload_identity in the SAME agents registry (POST
 * /agents with kind='workload_identity'). The workload identity (fed_issuer/fed_subject) + owner +
 * backup are the SA-specific birth requirements; autonomy is omitted so the server applies the
 * non-agent default ('bounded'). Step-up gated (agents.manage). Refreshes the registry list.
 */
export function useCreateWorkloadIdentityMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, CreateWorkloadIdentityInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: { ...input, kind: "workload_identity" },
        }).then((raw) => agentSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useReassignOwnerMutation re-anchors a principal to a new owner (+ a new backup for a service
 * account) — the offboarding-recovery ceremony (NHI S4c). POST .../agents/{id}/reassign-owner, gated
 * on principals.owner_reassign + step-up. Reactivates a suspended principal server-side. Returns the
 * updated agent; refreshes the detail + list. A retired principal, a broken owner invariant, or a
 * cross-tenant owner is a 4xx the caller surfaces as an error (never an optimistic cache write).
 */
export function useReassignOwnerMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, ReassignOwnerInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/reassign-owner`, {
          method: "POST",
          body: input,
        }).then((raw) => agentSchema.parse(raw)),
      ),
    onSuccess: (agent) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), agent);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/**
 * useAttestReviewMutation records a periodic-review AFFIRMATION ("reviewed, still correct"):
 * POST .../agents/{id}/attest-review re-stamps the review clock and audits agent.review_attested
 * WITHOUT changing any ceiling — the ~90% of recertifications that are affirmations, which the
 * tighten-only (no-op-rejecting) recertify path cannot express. Same governance gate as certify /
 * recertify (agents.certify + step-up). Refreshes the detail + list.
 */
export function useAttestReviewMutation(tenantID: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation<Agent, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(agentId)}/attest-review`, {
          method: "POST",
          body: {},
        }).then((raw) => agentSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.agents.detail(tenantID, agentId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

// ── Standing delegations (P6e) ────────────────────────────────────────────────
// The unattended pre-authorization surface. Create + revoke are agents.manage +
// step-up-gated (the revoke is the kill-switch); the list is agents.read oversight.

function standingBase(tenantID: string): string {
  return `${base(tenantID)}/standing-delegations`;
}

export async function fetchStandingDelegations(
  tenantID: string,
): Promise<StandingDelegationsList> {
  const raw = await apiFetch<unknown>(standingBase(tenantID));
  return standingDelegationsListSchema.parse(raw);
}

export function useStandingDelegationsQuery(tenantID: string) {
  return useQuery<StandingDelegationsList, ApiError>({
    queryKey: queryKeys.agents.standingDelegations(tenantID),
    queryFn: () => fetchStandingDelegations(tenantID),
    enabled: tenantID.length > 0,
  });
}

// "Who's using this shared budget": each pooled cap on a standing broken down by the acting agent.
export async function fetchStandingBudgetBreakdown(
  tenantID: string,
  standingID: string,
): Promise<BudgetBreakdown> {
  const raw = await apiFetch<unknown>(
    `${standingBase(tenantID)}/${encodeURIComponent(standingID)}/budget-breakdown`,
  );
  return budgetBreakdownSchema.parse(raw);
}

export function useStandingBudgetBreakdownQuery(
  tenantID: string,
  standingID: string,
  enabled = true,
) {
  return useQuery<BudgetBreakdown, ApiError>({
    queryKey: queryKeys.agents.standingBudgetBreakdown(tenantID, standingID),
    queryFn: () => fetchStandingBudgetBreakdown(tenantID, standingID),
    enabled: enabled && tenantID.length > 0 && standingID.length > 0,
  });
}

// The live crew on a standing: the real team, their shared budgets, and the decision play-by-play.
export async function fetchStandingCrewView(
  tenantID: string,
  standingID: string,
): Promise<CrewView> {
  const raw = await apiFetch<unknown>(
    `${standingBase(tenantID)}/${encodeURIComponent(standingID)}/crew`,
  );
  return crewViewSchema.parse(raw);
}

export function useStandingCrewViewQuery(tenantID: string, standingID: string, enabled = true) {
  return useQuery<CrewView, ApiError>({
    queryKey: queryKeys.agents.standingCrewView(tenantID, standingID),
    queryFn: () => fetchStandingCrewView(tenantID, standingID),
    enabled: enabled && tenantID.length > 0 && standingID.length > 0,
    // A crew's activity is live-ish while an incident runs; refresh on a modest cadence when open.
    refetchInterval: 15_000,
  });
}

export function useCreateStandingDelegationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<CreateStandingDelegationResponse, ApiError, CreateStandingDelegationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(standingBase(tenantID), { method: "POST", body: input }).then((raw) =>
          createStandingDelegationResponseSchema.parse(raw),
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.standingDelegations(tenantID) });
    },
  });
}

export function useRevokeStandingDelegationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${standingBase(tenantID)}/${encodeURIComponent(id)}`, {
          method: "DELETE",
          searchParams: { reason },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.agents.standingDelegations(tenantID) });
    },
  });
}
